import { z } from 'zod';

/** What a server key may call on `/server/v1` (BB-17). New scopes are added here with the route that needs them. */
export const SERVER_KEY_SCOPES = [
  'ingest.write',
  'profiles.write',
  'trigger.campaigns',
  'trigger.canvas',
  'trigger.transactional',
  'attribution.import',
  'export.read',
  'privacy.jobs',
] as const;
export type ServerKeyScope = (typeof SERVER_KEY_SCOPES)[number];

const IPV4 = /^(25[0-5]|2[0-4]\d|1\d\d|[1-9]?\d)(\.(25[0-5]|2[0-4]\d|1\d\d|[1-9]?\d)){3}$/;

/** 4, 6 or 0 — runtime-neutral (no node:net) so the dashboard can share the contract. */
function ipFamily(address: string): 4 | 6 | 0 {
  if (IPV4.test(address)) return 4;
  if (!address.includes(':') || !/^[0-9a-fA-F:.]+$/.test(address)) return 0;
  try {
    new URL(`http://[${address}]/`);
    return 6;
  } catch {
    return 0;
  }
}

/** An IPv4/IPv6 address or CIDR block (`203.0.113.7`, `10.0.0.0/8`, `2001:db8::/32`). */
export const Cidr = z.string().refine((value) => {
  const [address, bits, extra] = value.split('/');
  const family = ipFamily(address ?? '');
  if (!family || extra !== undefined) return false;
  if (bits === undefined) return true;
  const n = Number(bits);
  return /^\d+$/.test(bits) && n >= 0 && n <= (family === 4 ? 32 : 128);
}, 'expected an IP address or CIDR block');

export const CreateServerKeyBody = z.strictObject({
  name: z.string().trim().min(1).max(100),
  scopes: z.array(z.enum(SERVER_KEY_SCOPES)).min(1).transform((scopes) => [...new Set(scopes)].sort()),
  ipAllowlist: z.array(Cidr).max(50).optional(),
  expiresAt: z.iso.datetime({ offset: true }).optional(),
});
export type CreateServerKeyBody = z.infer<typeof CreateServerKeyBody>;

export const RotateServerKeyBody = z.strictObject({
  /** How long the old key keeps working after the new one is issued (0 = immediately invalid). */
  overlapHours: z.number().int().min(0).max(168).default(24),
});
